import { useParams } from "react-router-dom";
import { Sidebar } from '../dashboard_component';
import React, { useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import { saveAs } from 'file-saver';
import { cbtAPI } from '../../api';
import 'react-toastify/dist/ReactToastify.css';

const TestResult = () => {
  const [result, setResult] = useState([]);
  const [testName, setTestName] = useState('');
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [stats, setStats] = useState({
    totalTests: 0,
    averageScore: 0,
    highScore: 0,
    lowScore: 0
  });
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const { cbt_pss_code } = useParams();

  const handleToggle = () => setIsCollapsed(!isCollapsed);

  useEffect(() => {
    const fetchResults = async () => {
      try {
        setLoading(true);
        
        const response = await cbtAPI.getStudentResults(cbt_pss_code);
        
        // Extract data from response
        const apiData = response.data;
        const resultData = apiData?.data || [];
        
        setTestName(apiData?.cbt_name || '');
        setTotalStudents(apiData?.total_students || 0);
        
        if (Array.isArray(resultData)) {
          setResult(resultData);
          
          // Calculate statistics from the data array
          const scores = resultData
            .map(item => parseFloat(item.total_score || 0))
            .filter(score => !isNaN(score));
          
          if (scores.length > 0) {
            const total = scores.reduce((a, b) => a + b, 0);
            const avg = total / scores.length;
            const max = Math.max(...scores);
            const min = Math.min(...scores);
            
            setStats({
              totalTests: scores.length,
              averageScore: avg,
              highScore: max,
              lowScore: min
            });
          }
          
          toast.success('Results loaded successfully');
        } else {
          toast.warning('No test results found');
          setResult([]);
        }
      } catch (error) {
        console.error('Error fetching results:', error);
        const errorMsg = error.data?.detail || error.message || 'Failed to fetch results';
        toast.error(errorMsg);
        setResult([]);
      } finally {
        setLoading(false);
      }
    };
    
    if (cbt_pss_code) {
      fetchResults();
    }
  }, [cbt_pss_code]);

  const generateCSV = async () => {
    if (result.length === 0) {
      toast.warning("No data to export");
      return;
    }

    try {
      setExporting(true);
      
      // CSV headers matching backend fields
      const headers = [
        "Index", "Session ID", "Student Name", "User ID", "Test ID",
        "Score", "Percentage", "Grade", "Passed", "Attempts",
        "Submitted At", "Time Taken (sec)"
      ].join(",");
      
      const rows = result.map((item, index) => {
        const date = new Date(item.submitted_at);
        const formattedDate = date.toLocaleString();
        
        return [
          index + 1,
          `"${item.internal_session_id}"`,
          `"${item.student_name}"`,
          `"${item.user_id}"`,
          `"${item.test_id}"`,
          item.total_score.toFixed(2),
          item.percentage.toFixed(2),
          `"${item.grade || 'N/A'}"`,
          item.passed ? "Yes" : "No",
          item.attempts,
          `"${formattedDate}"`,
          item.time_taken
        ].join(",");
      });
      
      const csvContent = [headers, ...rows].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      saveAs(blob, `cbt_results_${cbt_pss_code}_${new Date().toISOString().split('T')[0]}.csv`);
      
      toast.success("CSV file downloaded successfully!");
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Failed to generate CSV");
    } finally {
      setExporting(false);
    }
  };

  const exportJSON = () => {
    if (result.length === 0) {
      toast.warning("No data to export");
      return;
    }
    
    const dataStr = JSON.stringify(result, null, 2);
    const blob = new Blob([dataStr], { type: "application/json" });
    saveAs(blob, `cbt_results_${cbt_pss_code}_${new Date().toISOString().split('T')[0]}.json`);
    toast.success("JSON file downloaded!");
  };

  const refreshData = async () => {
    setLoading(true);
    try {
      const response = await cbtAPI.getStudentResults(cbt_pss_code);
      const apiData = response.data;
      const resultData = apiData?.data || [];
      if (Array.isArray(resultData)) {
        setResult(resultData);
        setTestName(apiData?.cbt_name || '');
        setTotalStudents(apiData?.total_students || 0);
        toast.success('Data refreshed successfully');
      } else {
        throw new Error('Failed to refresh data');
      }
    } catch (error) {
      console.error('Refresh error:', error);
      toast.error(error.data?.detail || error.message || 'Failed to refresh data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/30">
      <Sidebar isCollapsed={isCollapsed} onToggle={handleToggle} />
      
      <div 
        className={`flex-1 transition-all duration-300 pt-16 lg:pt-8 ${
          isCollapsed ? 'lg:ml-20' : 'lg:ml-64'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header Section */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">
                  {testName ? `${testName} - Results` : 'Test Results Dashboard'}
                </h1>
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-gray-600">Passcode: </p>
                  <span className="font-mono bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 px-3 py-1 rounded-md border border-blue-200">
                    {cbt_pss_code}
                  </span>
                  <button 
                    onClick={refreshData}
                    className="ml-2 text-blue-600 hover:text-blue-800"
                    title="Refresh data"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <button 
                  className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-4 py-3 rounded-xl font-medium flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={generateCSV}
                  disabled={exporting || result.length === 0}
                >
                  {exporting ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Exporting...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      Export CSV
                    </>
                  )}
                </button>
                <button
                  onClick={exportJSON}
                  disabled={result.length === 0}
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-4 py-3 rounded-xl font-medium flex items-center gap-2 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                  </svg>
                  Export JSON
                </button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 shadow-sm border border-blue-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Tests</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">{stats.totalTests}</p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 shadow-sm border border-green-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Avg Score</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {stats.averageScore.toFixed(1)}
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 shadow-sm border border-purple-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-purple-600">High Score</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {stats.highScore.toFixed(1)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-purple-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 shadow-sm border border-orange-100">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Low Score</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {stats.lowScore.toFixed(1)}
                    </p>
                  </div>
                  <div className="p-3 bg-orange-100 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-orange-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Results Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Test Results</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Showing {result.length} test attempts
                    {loading && <span className="ml-2 text-blue-600">(Loading...)</span>}
                  </p>
                </div>
                <div className="mt-2 sm:mt-0">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm text-gray-500">Sort by:</span>
                    <select className="border border-gray-300 rounded-lg px-3 py-1 text-sm">
                      <option>Date (Newest)</option>
                      <option>Score (High to Low)</option>
                      <option>Score (Low to High)</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex flex-col justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
                  <p className="text-gray-600 font-medium">Loading test results...</p>
                  <p className="text-sm text-gray-500 mt-1">Please wait while we fetch the data</p>
                </div>
              ) : result.length > 0 ? (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Student</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Test ID</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">%</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Passed</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Attempts</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Submitted</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time (sec)</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {result.map((data, index) => (
                      <LoopResult key={data.internal_session_id} data={data} index={index} cbt_pass_code={cbt_pss_code} />
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center py-12">
                  <div className="w-24 h-24 mx-auto mb-4 text-gray-400">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Available</h3>
                  <p className="text-gray-500 max-w-sm mx-auto mb-6">
                    No test results found for the passcode: <span className="font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded">{cbt_pss_code}</span>
                  </p>
                  <button 
                    onClick={refreshData}
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    Check Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <ToastContainer 
          position="bottom-right"
          toastClassName="rounded-xl shadow-lg"
          progressClassName="bg-gradient-to-r from-blue-500 to-purple-500"
          autoClose={4000}
        />
      </div>
    </div>
  );
};

const LoopResult = ({ data, index, cbt_pass_code }) => {
  const [showModal, setShowModal] = useState(false);
  const [submittedAt, setSubmittedAt] = useState("");
  
  useEffect(() => {
    const formatDate = () => {
      if (!data.submitted_at) return;
      const date = new Date(data.submitted_at);
      const options = {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      };
      setSubmittedAt(date.toLocaleString('en-US', options));
    };
    
    formatDate();
  }, [data.submitted_at]);

  const getScoreColor = (score) => {
    const numScore = parseFloat(score);
    if (numScore >= 80) return "bg-gradient-to-r from-green-100 to-emerald-100 text-green-800 border-green-200";
    if (numScore >= 60) return "bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-800 border-blue-200";
    if (numScore >= 40) return "bg-gradient-to-r from-yellow-100 to-amber-100 text-yellow-800 border-yellow-200";
    return "bg-gradient-to-r from-red-100 to-pink-100 text-red-800 border-red-200";
  };

  const getStatusColor = (passed) => {
    return passed 
      ? "bg-green-100 text-green-800 border-green-200" 
      : "bg-red-100 text-red-800 border-red-200";
  };

  return (
    <>
      <tr className="hover:bg-gray-50/80 transition-colors duration-150 group">
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900 bg-gray-100 w-8 h-8 rounded-full flex items-center justify-center group-hover:bg-gray-200">
            {index + 1}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">
            {data.student_name || 'N/A'}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            <div className="p-1 bg-gray-100 rounded-lg mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
              </svg>
            </div>
            <span className="text-sm text-gray-700 truncate max-w-xs" title={data.student_email}>
              {/* {data.student_email ? `${data.student_email.substring(0, 12)}...` : 'N/A'} */}
              {data.student_email || 'N/A'}
            </span>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-mono text-gray-900 bg-gray-50 px-3 py-1 rounded-lg border group-hover:bg-gray-100">
            {data.test_id || 'N/A'}
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold border ${getScoreColor(data.total_score)}`}>
            {data.total_score.toFixed(1)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="text-sm font-medium text-gray-900">
            {data.percentage.toFixed(1)}%
          </div>
          <div className="w-full bg-gray-200 rounded-full h-1.5 mt-1">
            <div 
              className="bg-gradient-to-r from-blue-500 to-purple-600 h-1.5 rounded-full" 
              style={{ width: `${Math.min(100, data.percentage)}%` }}
            ></div>
          </div>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
            data.grade === 'Pass' ? 'bg-green-100 text-green-800 border-green-200' : 
            data.grade === 'Fail' ? 'bg-red-100 text-red-800 border-red-200' : 
            'bg-gray-100 text-gray-800 border-gray-200'
          }`}>
            {data.grade || 'N/A'}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap">
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(data.passed)}`}>
            {data.passed ? 'Yes' : 'No'}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {data.attempts}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium">
          {submittedAt}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {data.time_taken}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <div className="flex items-center gap-2">
            <button 
              className="inline-flex items-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-150 group/btn"
              onClick={() => setShowModal(true)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1 group-hover/btn:text-blue-600" viewBox="0 0 20 20" fill="currentColor">
                <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
              </svg>
              View
            </button>
            <a 
              href={`/cbt_creator/test_data/${cbt_pass_code}/${data.internal_session_id}/${data.user_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 transition-colors duration-150 shadow-sm"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
              </svg>
              Report
            </a>
          </div>
        </td>
      </tr>

      {/* Enhanced Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div 
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75 backdrop-blur-sm"
              onClick={() => setShowModal(false)}
            ></div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-r from-blue-100 to-indigo-100 rounded-xl">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Test Result Details</h3>
                    <p className="text-sm text-gray-500">Complete test information</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
              
              {/* Content */}
              <div className="space-y-6">
                {/* Score Highlight */}
                <div className="text-center py-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                  <div className="text-sm text-blue-600 font-medium mb-1">Final Score</div>
                  <div className="text-4xl font-bold text-gray-900">
                    {data.total_score.toFixed(1)}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Grade: <span className="font-bold">{data.grade || 'N/A'}</span> • 
                    Percentage: <span className="font-bold">{data.percentage.toFixed(1)}%</span>
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Session ID</span>
                    <span className="font-mono text-sm bg-white px-2 py-1 rounded border truncate max-w-xs">
                      {data.internal_session_id}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Student Name</span>
                    <span className="text-sm font-medium">{data.student_name}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">User ID</span>
                    <span className="font-mono text-sm bg-white px-2 py-1 rounded border truncate max-w-xs">
                      {data.student_email || 'N/A'}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Test ID</span>
                    <span className="font-mono text-sm bg-white px-2 py-1 rounded border truncate max-w-xs">
                      {data.test_id}
                    </span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Attempts</span>
                    <span className="text-sm font-medium">{data.attempts}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Time Taken (sec)</span>
                    <span className="text-sm font-medium">{data.time_taken}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Date Completed</span>
                    <span className="text-sm font-medium">{submittedAt}</span>
                  </div>
                  
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <span className="text-sm font-medium text-gray-600">Status</span>
                    <span className={`text-sm font-medium px-2 py-1 rounded ${getStatusColor(data.passed)}`}>
                      {data.passed ? 'Passed' : 'Failed'}
                    </span>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4">
                  <a 
                    href={`/cbt_creator/test_data/${cbt_pass_code}/${data.internal_session_id}/${data.user_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-4 py-3 rounded-xl font-medium transition-colors duration-200 shadow-sm"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M2 11a1 1 0 011-1h2a1 1 0 011 1v5a1 1 0 01-1 1H3a1 1 0 01-1-1v-5zM8 7a1 1 0 011-1h2a1 1 0 011 1v9a1 1 0 01-1 1H9a1 1 0 01-1-1V7zM14 4a1 1 0 011-1h2a1 1 0 011 1v12a1 1 0 01-1 1h-2a1 1 0 01-1-1V4z" />
                    </svg>
                    View Full Report
                  </a>
                  <button 
                    onClick={() => setShowModal(false)}
                    className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-3 rounded-xl font-medium transition-colors duration-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export { TestResult };